import { Text } from '../../cdl/Text/Text';
import { formatDate } from '../../common/helpers/formatDate.helper';
import { formatMoney } from '../../common/helpers/formatMoney.helper';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { translate } from '../../common/helpers/translate.helper';
import { hasSupplierSubscriptionPlanSpecialOffer } from '../hasSupplierSubscriptionPlanSpecialOffer';
import { SupplierSubscriptionPlanMessage } from '../types/SupplierSubscriptionPlanMessage';

import { SupplierSubscriptionPlanPricing } from './SupplierSubscriptionPlanPricing';

interface ProfessionalSubscriptionPlanPricingProps {
    plan: SupplierSubscriptionPlanMessage;
}

export const ProfessionalSubscriptionPlanPricing = ({ plan }: ProfessionalSubscriptionPlanPricingProps) => {
    const hasSpecialOffer = hasSupplierSubscriptionPlanSpecialOffer(plan);
    const monthlyPrice = hasSpecialOffer ? plan.monthlySpecialOfferPrice ?? plan.monthlyPrice : plan.monthlyPrice;
    const annualPrice = hasSpecialOffer ? plan.annualSpecialOfferPrice ?? plan.annualPrice : plan.annualPrice;
    const transactionFee = hasSpecialOffer
        ? plan.specialOfferTransactionFeePercentage ?? plan.transactionFeePercentage
        : plan.transactionFeePercentage;

    if (!monthlyPrice || !annualPrice || !plan.monthlyPrice || !plan.annualPrice) {
        return null;
    }

    return (
        <SupplierSubscriptionPlanPricing
            offerLabel={
                hasSpecialOffer && plan.specialOfferValidUntil !== undefined
                    ? translate('supplierSubscriptionPlans.firstYearOffer', {
                          date: formatDate({ date: plan.specialOfferValidUntil, timeZone: 'UTC' }),
                      })
                    : undefined
            }
            price={
                <>
                    <Text variant="title">
                        {translate('supplierSubscriptionPlans.pricePerMonth', { price: formatMoney(monthlyPrice) })}
                    </Text>
                    {hasSpecialOffer && plan.monthlySpecialOfferPrice !== undefined ? (
                        <Text as="s" variant="extraSmall" color="foreground.muted">
                            {translate('supplierSubscriptionPlans.pricePerMonth', {
                                price: formatMoney(plan.monthlyPrice),
                            })}
                        </Text>
                    ) : null}
                </>
            }
            priceNote={
                <>
                    <Text variant="extraSmall" color="foreground.muted">
                        {translate('supplierSubscriptionPlans.annualBilling', { price: formatMoney(annualPrice) })}
                    </Text>
                    {hasSpecialOffer && plan.annualSpecialOfferPrice !== undefined ? (
                        <Text as="s" variant="extraSmall" color="foreground.muted">
                            {translate('supplierSubscriptionPlans.pricePerYear', {
                                price: formatMoney(plan.annualPrice),
                            })}
                        </Text>
                    ) : null}
                </>
            }
            transactionFee={
                <>
                    <Text variant="fieldLabel">
                        {translate('supplierSubscriptionPlans.transactionFee', {
                            percentage: formatNumber({ number: transactionFee, minimumFractionDigits: 1 }),
                        })}
                    </Text>
                    {hasSpecialOffer && plan.specialOfferTransactionFeePercentage !== undefined ? (
                        <Text as="s" variant="extraSmall" color="foreground.muted">
                            {translate('supplierPlan.feePercentage', {
                                percentage: formatNumber({
                                    number: plan.transactionFeePercentage,
                                    minimumFractionDigits: 1,
                                }),
                            })}
                        </Text>
                    ) : null}
                </>
            }
        />
    );
};
